import {
  Box,
  BoxProps,
  Flex,
  LinkAnchor,
  mdBumps,
  smBumps,
  Typo,
  xlBumps,
} from '@wrisk/ui-components'
import React, { useCallback } from 'react'

import { CoverOption, Quote } from '../../../../domain'
import {
  TKey,
  TKeyBuilder,
  useWriskTranslation,
} from '../../../../infrastructure/internationalisation'
import { WriskTrans } from '../../trans'
import { useOptionalCoverModal } from '../useOptionalCoverModal'
import { AddOnButtons } from './components/Buttons'
import { AddOnHeader } from './components/Header'

interface AddOnSelectorProps extends BoxProps {
  quote: Quote
  coverOption: CoverOption
  onChange: (cover: CoverOption) => void
  tKey: TKeyBuilder
}

const tCovers = TKey('covers')

export const AddOnBoolean: React.FC<AddOnSelectorProps> = ({
  quote,
  coverOption,
  onChange,
  tKey,
  ...props
}) => {
  const { t } = useWriskTranslation()

  const id = `section-add-on-${coverOption.coverOption}`

  const onChangeCallback = useCallback(
    (active: boolean | undefined) =>
      coverOption.available && onChange({ ...coverOption, active }),
    [onChange, coverOption],
  )

  const onLinkMoreClick = useOptionalCoverModal(quote)

  return coverOption.available ? (
    <Box data-testid={coverOption.coverOption} mb={xlBumps} width={1} {...props}>
      <Flex flexDirection='column' justifyContent='flex-start' mb={2} variant='raised'>
        <AddOnHeader
          tKey={tKey}
          amount={coverOption.premium}
          disabled={false}
          text={t<string>(tKey('add-ons', coverOption.coverOption, 'header'))}
        />
        <Box px={mdBumps} pb={mdBumps} width={1}>
          <Typo mb={smBumps} whiteSpace='pre-line'>
            <WriskTrans t={t} i18nKey={tCovers(coverOption.coverOption, 'description')} />
          </Typo>
          {t(tCovers(coverOption.coverOption, 'hint'), { defaultValue: '' }) && (
            <Typo mb={smBumps} whiteSpace='pre-line'>
              <WriskTrans t={t} i18nKey={tCovers(coverOption.coverOption, 'hint')} />
            </Typo>
          )}
          <LinkAnchor
            typoSize='sm'
            onClick={() => onLinkMoreClick(coverOption)}
            variant='standalone'
          >
            {t(tKey('add-ons', coverOption.coverOption, 'actions.more'))}
          </LinkAnchor>
        </Box>
      </Flex>
      <AddOnButtons
        selected={coverOption.active}
        prefixForIds={id}
        titleYes={t(tKey('add-ons', coverOption.coverOption, 'actions.yes'))}
        titleNo={t(tKey('add-ons', coverOption.coverOption, 'actions.no'))}
        onSelect={onChangeCallback}
      />
    </Box>
  ) : (
    <Box mb={xlBumps} width={1} {...props}>
      <Flex flexDirection='column' justifyContent='flex-start' mb={2} variant='raised'>
        <AddOnHeader
          tKey={tKey}
          disabled={true}
          text={t<string>(tCovers(coverOption.coverOption, 'name'))}
        />
        <Box px={mdBumps} pb={mdBumps} width={1}>
          <Typo>{t(tCovers(coverOption.coverOption, 'unavailable', 'description'))}</Typo>
        </Box>
      </Flex>
    </Box>
  )
}
